// ============================================================================
// DRAWING BATCH OPERATIONS - Bulk actions for multiple drawings
// Date: January 2026
// ============================================================================

'use client';

import React, { useState, useMemo } from 'react';
import {
  Download, Trash2, FolderInput, Tag, CheckCircle, AlertCircle,
  X, Loader2, Archive, FileText, Share2, Printer, RefreshCw,
  ChevronDown, Users, Mail, Lock, Unlock, Eye, EyeOff, Briefcase,
} from 'lucide-react';
import type { Drawing, DrawingFolder, DrawingStatus } from './types';
import { STATUS_CONFIG, DRAWING_DISCIPLINES } from './types';

// ============================================================================
// TYPES
// ============================================================================

interface DrawingBatchOperationsProps {
  selectedDrawings: Drawing[];
  folders: DrawingFolder[];
  onMoveToFolder: (drawingIds: string[], folderId: string | null) => Promise<void>;
  onBulkDelete: (drawingIds: string[]) => Promise<void>;
  onBulkStatusChange: (drawingIds: string[], status: string) => Promise<void>;
  onBulkTagAdd: (drawingIds: string[], tags: string[]) => Promise<void>;
  onBulkDownload: (drawingIds: string[]) => Promise<void>;
  /** When omitted, bulk email/share is hidden (use Work Item Preview for distribution). */
  onBulkShare?: (drawingIds: string[], recipients: string[]) => Promise<void>;
  onBulkPrint: (drawingIds: string[]) => Promise<void>;
  onBulkLinkJob: (drawingIds: string[], workItemId: string) => Promise<void>;
  onCreateTransmittal: (drawingIds: string[]) => void;
  onClearSelection: () => void;
  isOpen: boolean;
}

type BatchAction = 
  | 'move' 
  | 'delete' 
  | 'status' 
  | 'tags' 
  | 'download' 
  | 'share' 
  | 'print' 
  | 'transmittal'
  | 'link_job';

interface BatchActionConfig {
  id: BatchAction;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  description: string;
  requiresConfirm?: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const BATCH_ACTIONS: BatchActionConfig[] = [
  {
    id: 'download',
    label: 'Download',
    icon: Download,
    color: 'text-blue-400',
    description: 'Download selected drawings as ZIP'
  },
  {
    id: 'move',
    label: 'Move to Folder',
    icon: FolderInput,
    color: 'text-amber-400',
    description: 'Move to a different folder'
  },
  {
    id: 'link_job',
    label: 'Link to Job',
    icon: Briefcase,
    color: 'text-sky-400',
    description: 'Attach to a work item (job) by ID'
  },
  {
    id: 'status',
    label: 'Change Status',
    icon: RefreshCw,
    color: 'text-purple-400',
    description: 'Update drawing status'
  },
  {
    id: 'tags',
    label: 'Add Tags',
    icon: Tag,
    color: 'text-green-400',
    description: 'Add tags to drawings'
  },
  {
    id: 'share',
    label: 'Share',
    icon: Share2,
    color: 'text-cyan-400',
    description: 'Share with team members'
  },
  {
    id: 'transmittal',
    label: 'Create Transmittal',
    icon: FileText,
    color: 'text-indigo-400',
    description: 'Generate transmittal document'
  },
  {
    id: 'print',
    label: 'Print',
    icon: Printer,
    color: 'text-gray-400',
    description: 'Send to printer'
  },
  {
    id: 'delete',
    label: 'Delete',
    icon: Trash2,
    color: 'text-red-400',
    description: 'Permanently delete drawings',
    requiresConfirm: true
  }
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-500' },
  { value: 'pending_review', label: 'Pending Review', color: 'bg-amber-500' },
  { value: 'approved', label: 'Approved', color: 'bg-blue-500' },
  { value: 'published', label: 'Published', color: 'bg-green-500' },
  { value: 'superseded', label: 'Superseded', color: 'bg-purple-500' },
  { value: 'archived', label: 'Archived', color: 'bg-gray-600' }
];

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

interface FolderSelectorProps {
  folders: DrawingFolder[];
  selectedFolderId: string | null;
  onSelect: (id: string | null) => void;
}

function FolderSelector({ folders, selectedFolderId, onSelect }: FolderSelectorProps) {
  const renderFolder = (folder: DrawingFolder, level: number = 0) => {
    const children = folders.filter(f => f.parent_id === folder.id);
    
    return (
      <div key={folder.id}>
        <button
          onClick={() => onSelect(folder.id)}
          className={`w-full text-left px-3 py-2 hover:bg-gray-700 flex items-center gap-2 ${
            selectedFolderId === folder.id ? 'bg-blue-600/20 text-blue-400' : 'text-gray-300'
          }`}
          style={{ paddingLeft: `${12 + level * 16}px` }}
        >
          <span className="text-lg">📁</span>
          <span className="truncate">{folder.name}</span>
        </button>
        {children.map(child => renderFolder(child, level + 1))}
      </div>
    );
  };

  const rootFolders = folders.filter(f => !f.parent_id);

  return (
    <div className="max-h-48 overflow-y-auto border border-gray-700 rounded-lg">
      <button
        onClick={() => onSelect(null)}
        className={`w-full text-left px-3 py-2 hover:bg-gray-700 flex items-center gap-2 border-b border-gray-700 ${
          selectedFolderId === null ? 'bg-blue-600/20 text-blue-400' : 'text-gray-300'
        }`}
      >
        <span className="text-lg">📂</span>
        <span>Root (No Folder)</span>
      </button>
      {rootFolders.map(folder => renderFolder(folder))}
    </div>
  );
}

interface TagInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  suggestions?: string[];
}

function TagInput({ tags, onChange, suggestions = [] }: TagInputProps) {
  const [input, setInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const handleAddTag = (tag: string) => {
    const trimmed = tag.trim().toLowerCase();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
    }
    setInput('');
    setShowSuggestions(false);
  };

  const handleRemoveTag = (tag: string) => {
    onChange(tags.filter(t => t !== tag));
  };

  const filteredSuggestions = suggestions.filter(
    s => s.toLowerCase().includes(input.toLowerCase()) && !tags.includes(s.toLowerCase())
  );

  return (
    <div className="relative">
      <div className="flex flex-wrap gap-2 p-2 bg-gray-800 border border-gray-700 rounded-lg min-h-[42px]">
        {tags.map(tag => (
          <span
            key={tag}
            className="flex items-center gap-1 px-2 py-1 bg-blue-600/20 text-blue-400 rounded text-sm"
          >
            {tag}
            <button onClick={() => handleRemoveTag(tag)} className="hover:text-blue-200">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={input}
          onChange={(e) => { setInput(e.target.value); setShowSuggestions(true); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && input) {
              e.preventDefault();
              handleAddTag(input);
            }
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={tags.length === 0 ? "Type tags and press Enter..." : ""}
          className="flex-1 min-w-[120px] bg-transparent text-white text-sm outline-none"
        />
      </div>
      
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-10 max-h-32 overflow-y-auto">
          {filteredSuggestions.slice(0, 5).map(suggestion => (
            <button
              key={suggestion}
              onClick={() => handleAddTag(suggestion)}
              className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-700"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface EmailInputProps {
  emails: string[];
  onChange: (emails: string[]) => void;
}

function EmailInput({ emails, onChange }: EmailInputProps) {
  const [input, setInput] = useState('');

  const handleAddEmail = (email: string) => {
    const trimmed = email.trim().toLowerCase();
    if (trimmed && trimmed.includes('@') && !emails.includes(trimmed)) {
      onChange([...emails, trimmed]);
    }
    setInput('');
  };

  const handleRemoveEmail = (email: string) => {
    onChange(emails.filter(e => e !== email));
  };

  return (
    <div className="flex flex-wrap gap-2 p-2 bg-gray-800 border border-gray-700 rounded-lg min-h-[42px]">
      {emails.map(email => (
        <span
          key={email}
          className="flex items-center gap-1 px-2 py-1 bg-green-600/20 text-green-400 rounded text-sm"
        >
          <Mail className="w-3 h-3" />
          {email}
          <button onClick={() => handleRemoveEmail(email)} className="hover:text-green-200">
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        type="email"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && input) {
            e.preventDefault();
            handleAddEmail(input);
          }
        }}
        placeholder={emails.length === 0 ? "Enter email addresses..." : ""}
        className="flex-1 min-w-[180px] bg-transparent text-white text-sm outline-none"
      />
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function DrawingBatchOperations({
  selectedDrawings,
  folders,
  onMoveToFolder,
  onBulkDelete,
  onBulkStatusChange,
  onBulkTagAdd,
  onBulkDownload,
  onBulkShare,
  onBulkPrint,
  onBulkLinkJob,
  onCreateTransmittal,
  onClearSelection,
  isOpen
}: DrawingBatchOperationsProps) {
  const [activeAction, setActiveAction] = useState<BatchAction | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  
  // Action-specific state
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [tagsToAdd, setTagsToAdd] = useState<string[]>([]);
  const [shareEmails, setShareEmails] = useState<string[]>([]);
  const [linkJobWorkItemId, setLinkJobWorkItemId] = useState('');

  const batchActionsVisible = useMemo(
    () => BATCH_ACTIONS.filter((action) => action.id !== 'share' || onBulkShare),
    [onBulkShare],
  );

  if (!isOpen || selectedDrawings.length === 0) return null;

  const handleExecuteAction = async () => {
    if (!activeAction) return;
    
    setIsProcessing(true);
    try {
      const ids = selectedDrawings.map(d => d.id);
      
      switch (activeAction) {
        case 'download':
          await onBulkDownload(ids);
          break;
        case 'move':
          await onMoveToFolder(ids, selectedFolderId);
          break;
        case 'status':
          if (selectedStatus) await onBulkStatusChange(ids, selectedStatus);
          break;
        case 'tags':
          if (tagsToAdd.length > 0) await onBulkTagAdd(ids, tagsToAdd);
          break;
        case 'share':
          if (shareEmails.length > 0 && onBulkShare) await onBulkShare(ids, shareEmails);
          break;
        case 'link_job':
          if (linkJobWorkItemId.trim()) {
            await onBulkLinkJob(ids, linkJobWorkItemId.trim());
          }
          break;
        case 'print':
          await onBulkPrint(ids);
          break;
        case 'transmittal':
          onCreateTransmittal(ids);
          break;
        case 'delete':
          if (showConfirm) {
            await onBulkDelete(ids);
            setShowConfirm(false);
          } else {
            setShowConfirm(true);
            setIsProcessing(false);
            return;
          }
          break;
      }
      
      // Reset state after successful action
      setActiveAction(null);
      setSelectedFolderId(null);
      setSelectedStatus('');
      setTagsToAdd([]);
      setShareEmails([]);
      setLinkJobWorkItemId('');
    } catch (error) {
      console.error('Batch action failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const renderActionPanel = () => {
    if (!activeAction) return null;

    switch (activeAction) {
      case 'move':
        return (
          <div className="p-4 border-t border-gray-800">
            <h4 className="text-sm font-medium text-white mb-3">Select destination folder:</h4>
            <FolderSelector
              folders={folders}
              selectedFolderId={selectedFolderId}
              onSelect={setSelectedFolderId}
            />
          </div>
        );

      case 'link_job':
        return (
          <div className="p-4 border-t border-gray-800">
            <h4 className="text-sm font-medium text-white mb-3">Work item (job) ID</h4>
            <p className="text-xs text-gray-500 mb-2">
              Paste the job / work item UUID from Total Operations (e.g. from the job URL or Smart Task). Uses{' '}
              <code className="text-gray-400">POST /api/v1/work-items/&#123;id&#125;/drawings</code>.
            </p>
            <input
              type="text"
              value={linkJobWorkItemId}
              onChange={(e) => setLinkJobWorkItemId(e.target.value)}
              placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000"
              className="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-white placeholder:text-gray-500"
            />
          </div>
        );

      case 'status':
        return (
          <div className="p-4 border-t border-gray-800">
            <h4 className="text-sm font-medium text-white mb-3">Select new status:</h4>
            <div className="grid grid-cols-2 gap-2">
              {STATUS_OPTIONS.map(status => (
                <button
                  key={status.value}
                  onClick={() => setSelectedStatus(status.value)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                    selectedStatus === status.value
                      ? 'border-blue-500 bg-blue-600/20'
                      : 'border-gray-700 hover:border-gray-600'
                  }`}
                >
                  <div className={`w-3 h-3 rounded-full ${status.color}`} />
                  <span className="text-sm text-white">{status.label}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 'tags':
        return (
          <div className="p-4 border-t border-gray-800">
            <h4 className="text-sm font-medium text-white mb-3">Add tags:</h4>
            <TagInput
              tags={tagsToAdd}
              onChange={setTagsToAdd}
              suggestions={['floor-plan', 'elevation', 'section', 'detail', 'structural', 'electrical', 'mechanical', 'plumbing', 'fire', 'as-built']}
            />
          </div>
        );

      case 'share':
        return (
          <div className="p-4 border-t border-gray-800">
            <h4 className="text-sm font-medium text-white mb-3">Share with:</h4>
            <EmailInput emails={shareEmails} onChange={setShareEmails} />
            <p className="text-xs text-gray-500 mt-2">Recipients will receive an email with download links</p>
          </div>
        );

      case 'delete':
        if (showConfirm) {
          return (
            <div className="p-4 border-t border-red-800 bg-red-900/20">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-red-400">Confirm Deletion</h4>
                  <p className="text-sm text-gray-400 mt-1">
                    Are you sure you want to permanently delete {selectedDrawings.length} drawing{selectedDrawings.length > 1 ? 's' : ''}? This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
          );
        }
        return null;

      default:
        return null;
    }
  };

  const canExecute = () => {
    switch (activeAction) {
      case 'status':
        return !!selectedStatus;
      case 'link_job':
        return linkJobWorkItemId.trim().length > 0;
      case 'tags':
        return tagsToAdd.length > 0;
      case 'share':
        return shareEmails.length > 0;
      case 'delete':
        return true;
      default:
        return true;
    }
  };

  // Get unique tags from selected drawings
  const existingTags = [...new Set(selectedDrawings.flatMap(d => d.tags || []))];
  
  // Get status breakdown
  const statusBreakdown = selectedDrawings.reduce((acc, d) => {
    acc[d.status] = (acc[d.status] || 0) + 1;
    return acc;
  }, {} as Partial<Record<DrawingStatus, number>>);

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-700 shadow-2xl z-[190] animate-slide-up">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-blue-400" />
            <span className="font-semibold text-white">
              {selectedDrawings.length} drawing{selectedDrawings.length > 1 ? 's' : ''} selected
            </span>
          </div>
          
          {/* Status breakdown */}
          <div className="flex items-center gap-2">
            {Object.entries(statusBreakdown).map(([status, count]) => {
              const config = STATUS_CONFIG[status as DrawingStatus] || STATUS_CONFIG.draft;
              return (
                <span
                  key={status}
                  className={`text-xs px-2 py-0.5 rounded ${config.bgColor} ${config.color}`}
                >
                  {count} {config.label}
                </span>
              );
            })}
          </div>
        </div>

        <button
          onClick={onClearSelection}
          className="flex items-center gap-1 text-sm text-gray-400 hover:text-white"
        >
          <X className="w-4 h-4" />
          Clear selection
        </button>
      </div>

      {/* Action buttons */}
      <div className="px-4 py-3">
        <div className="flex items-center gap-2 flex-wrap">
          {batchActionsVisible.map(action => {
            const Icon = action.icon;
            const isActive = activeAction === action.id;
            
            return (
              <button
                key={action.id}
                onClick={() => setActiveAction(isActive ? null : action.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-gray-700 ring-2 ring-blue-500'
                    : 'bg-gray-800 hover:bg-gray-700'
                }`}
                title={action.description}
              >
                <Icon className={`w-4 h-4 ${action.color}`} />
                <span className="text-sm text-white">{action.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Action panel */}
      {renderActionPanel()}

      {/* Execute button */}
      {activeAction && (
        <div className="px-4 py-3 border-t border-gray-800 flex items-center justify-between">
          <button
            onClick={() => {
              setActiveAction(null);
              setShowConfirm(false);
            }}
            className="px-4 py-2 text-gray-400 hover:text-white"
          >
            Cancel
          </button>
          
          <button
            onClick={handleExecuteAction}
            disabled={isProcessing || !canExecute()}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 ${
              activeAction === 'delete'
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                {activeAction === 'delete' && showConfirm ? 'Yes, Delete' : `Apply to ${selectedDrawings.length} drawings`}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
